import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronRight, Copy } from 'lucide-react';
import { useState } from 'react';
import { ExportMenu, ListPageShell, PageHeader, Table } from '~/components/data';
import { AIInsightCard } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, Drawer, Input, Select } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import { type EvidenceFilter, fetchEvidence } from '~/lib/api/evidence';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/evidence')({ component: EvidenceExplorer });

function EvidenceExplorer(): JSX.Element {
  const [filter, setFilter] = useState<EvidenceFilter>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const toast = useToast();

  // FE-147 Sprint Q+: copy hash to clipboard affordance.
  function copyHash(hash: string): void {
    void navigator.clipboard.writeText(hash).then(() => {
      toast.push({ variant: 'success', title: m.relative_time_just_now() });
    });
  }

  const q = useQuery({
    queryKey: ['evidence', filter],
    queryFn: () => fetchEvidence(filter),
  });
  const state = useListPageState(q);

  const items = q.data?.items ?? [];
  const selected = items.find((e) => e.id === selectedId);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.evidence_page_title()}
        description={m.evidence_page_description()}
        actions={
          <ExportMenu resource="audit" filters={filter as Record<string, string | undefined>} />
        }
      />
      <section className={shared.sectionCard}>
        <h3 className={shared.sectionHeading}>{m.evidence_filters_heading()}</h3>
        <div className={shared.formGrid3}>
          <Select
            label={m.evidence_source_label()}
            value={filter.source ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value || undefined }))}
            options={[
              { value: '', label: m.evidence_source_all() },
              { value: 'staiter', label: 'STAITER' },
              { value: '1c', label: '1С' },
              { value: 'bitrix', label: 'Bitrix' },
              { value: 'adesk', label: 'Adesk' },
              { value: 'excel', label: 'Excel' },
            ]}
          />
          <Input
            label={m.evidence_doc_id_label()}
            value={filter.document_id ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, document_id: e.target.value || undefined }))}
            className={shared.mono}
            placeholder={m.evidence_doc_id_placeholder()}
          />
          <Input
            label={m.evidence_hash_label()}
            value={filter.hash ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, hash: e.target.value || undefined }))}
            className={shared.mono}
            placeholder="SHA-256"
          />
        </div>
      </section>

      {/* FE-116: AI Insight Card pattern для dedup hint (вместо plain info-bg-15 div).
          Per STAITER_UI §AI-content — italic + agent border + дисклеймер. */}
      <AIInsightCard title="Дедупликация evidence" body={<span>{m.evidence_dedup_hint()}</span>} />

      <ListPageShell
        state={state}
        emptyTitle={m.evidence_empty_title()}
        emptyDescription={m.evidence_empty_desc()}
      >
        {(rows) => (
          <Table
            ariaLabel={m.evidence_page_title()}
            data={rows}
            rowKey={(e) => e.id}
            // FE-147 Sprint Q+: row-click pattern instead of «Открыть» button.
            onRowClick={(e) => setSelectedId(e.id)}
            columns={[
              {
                id: 'source',
                header: m.evidence_col_source(),
                accessor: (e) => (
                  <ProvenanceChip
                    source={e.source}
                    {...(e.document_id ? { document_id: e.document_id } : {})}
                    {...(e.fetched_at ? { fetched_at: e.fetched_at } : {})}
                  />
                ),
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (e: (typeof rows)[number]) =>
                        (e.entity_id ? entities[e.entity_id]?.name : undefined) ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              { id: 'label', header: m.evidence_col_label(), accessor: (e) => e.label ?? '—' },
              {
                id: 'snippet',
                header: m.evidence_col_snippet(),
                accessor: (e) => e.snippet ?? '—',
              },
              {
                id: 'open',
                header: '',
                accessor: () => (
                  <span style={{ color: 'var(--text-muted)' }} aria-hidden="true">
                    <ChevronRight size={16} />
                  </span>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>

      <Drawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        title={selected?.label ?? m.evidence_drawer_default()}
        width={520}
      >
        {selected && (
          <div className={shared.page}>
            <ProvenanceChip
              source={selected.source}
              {...(selected.document_id ? { document_id: selected.document_id } : {})}
              {...(selected.fetched_at ? { fetched_at: selected.fetched_at } : {})}
            />
            {/* FE-147 Sprint Q+: copy-hash CTA для audit trail. */}
            {selected.hash && (
              <Button
                size="sm"
                variant="ghost"
                leadingIcon={<Copy size={14} />}
                onClick={() => copyHash(selected.hash!)}
              >
                {selected.hash.slice(0, 16)}…
              </Button>
            )}
            {selected.snippet && <pre className={shared.jsonPre}>{selected.snippet}</pre>}
            {(selected.linked_run_ids ?? []).length > 0 && (
              <div>
                <h3 className={shared.sectionHeading}>{m.evidence_linked_runs()}</h3>
                <ul>
                  {(selected.linked_run_ids ?? []).map((id) => (
                    <li key={id} className={shared.mono}>
                      {id}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
